const API_BASE = (import.meta as any).env?.VITE_API_BASE_URL || "/api";

export type ZodiacSign = {
  id: number;
  signId: string;
  name: string;
  nameTamil?: string;
  nameGerman?: string;
  symbol?: string;
  symbolTamil?: string;
  dates?: string;
  element?: string;
  elementTamil?: string;
  elementGerman?: string;
  planet?: string;
  planetTamil?: string;
  planetGerman?: string;
  color?: string;
  colorTamil?: string;
  colorGerman?: string;
  luckyNumber?: number;
  description?: string;
  descriptionTamil?: string;
  descriptionGerman?: string;
  traits?: string;
  traitsTamil?: string;
  traitsGerman?: string;
  createdAt?: string;
};

export type HoroscopePrediction = {
  id: number;
  signId: string;
  date?: string;
  period?: string;
  periodTamil?: string;
  periodGerman?: string;
  overall?: number;
  love?: number;
  career?: number;
  health?: number;
  finance?: number;
  prediction?: string;
  predictionTamil?: string;
  predictionGerman?: string;
  advice?: string;
  adviceTamil?: string;
  adviceGerman?: string;
  luckyColor?: string;
  luckyColorTamil?: string;
  luckyColorGerman?: string;
  luckyNumber?: number;
  auspiciousTime?: string;
  auspiciousTimeTamil?: string;
  auspiciousTimeGerman?: string;
  createdAt?: string;
};

export type BakthiContent = {
  id: number;
  title: string;
  titleTamil?: string;
  titleGerman?: string;
  content?: string;
  contentTamil?: string;
  contentGerman?: string;
  category?: string;
  categoryTamil?: string;
  categoryGerman?: string;
  imageUrl?: string;
  publishDate?: string;
  author?: string;
  tags?: string;
  isFeatured?: boolean;
  createdAt?: string;
};

export async function fetchAllZodiacSigns(): Promise<ZodiacSign[]> {
  try {
    const res = await fetch(`${API_BASE}/zodiac-signs`);
    if (!res.ok) throw new Error("zodiac signs load failed");
    return res.json();
  } catch {
    return [];
  }
}

export async function getZodiacSign(id: number): Promise<ZodiacSign | null> {
  try {
    const res = await fetch(`${API_BASE}/zodiac-signs/${id}`);
    if (!res.ok) return null;
    return res.json();
  } catch {
    return null;
  }
}

export async function getZodiacSignBySignId(
  signId: string
): Promise<ZodiacSign | null> {
  try {
    const res = await fetch(
      `${API_BASE}/zodiac-signs/sign/${encodeURIComponent(signId)}`
    );
    if (!res.ok) return null;
    return res.json();
  } catch {
    return null;
  }
}

export async function searchZodiacSigns(query: string): Promise<ZodiacSign[]> {
  try {
    const res = await fetch(
      `${API_BASE}/zodiac-signs/search?q=${encodeURIComponent(query)}`
    );
    if (!res.ok) throw new Error("zodiac signs search failed");
    return res.json();
  } catch {
    return [];
  }
}

export async function getZodiacSignsByElement(
  element: string
): Promise<ZodiacSign[]> {
  try {
    const res = await fetch(
      `${API_BASE}/zodiac-signs/element/${encodeURIComponent(element)}`
    );
    if (!res.ok) throw new Error("zodiac signs by element failed");
    return res.json();
  } catch {
    return [];
  }
}

export async function getElements(): Promise<string[]> {
  try {
    const res = await fetch(`${API_BASE}/zodiac-signs/elements`);
    if (!res.ok) throw new Error("elements failed");
    return res.json();
  } catch {
    return [];
  }
}

export async function fetchAllHoroscopePredictions(): Promise<
  HoroscopePrediction[]
> {
  try {
    const res = await fetch(`${API_BASE}/horoscope-predictions`);
    if (!res.ok) throw new Error("horoscope predictions load failed");
    return res.json();
  } catch {
    return [];
  }
}

export async function getHoroscopePrediction(
  id: number
): Promise<HoroscopePrediction | null> {
  try {
    const res = await fetch(`${API_BASE}/horoscope-predictions/${id}`);
    if (!res.ok) return null;
    return res.json();
  } catch {
    return null;
  }
}

export async function getHoroscopePredictionsBySignId(
  signId: string
): Promise<HoroscopePrediction[]> {
  try {
    const res = await fetch(
      `${API_BASE}/horoscope-predictions/sign/${encodeURIComponent(signId)}`
    );
    if (!res.ok) throw new Error("horoscope predictions by sign failed");
    return res.json();
  } catch {
    return [];
  }
}

export async function getHoroscopePredictionsBySignIdAndPeriod(
  signId: string,
  period: string
): Promise<HoroscopePrediction[]> {
  try {
    const res = await fetch(
      `${API_BASE}/horoscope-predictions/sign/${encodeURIComponent(
        signId
      )}/period/${encodeURIComponent(period)}`
    );
    if (!res.ok)
      throw new Error("horoscope predictions by sign and period failed");
    return res.json();
  } catch {
    return [];
  }
}

export async function getHoroscopePredictionsByDate(
  date: string
): Promise<HoroscopePrediction[]> {
  try {
    const res = await fetch(
      `${API_BASE}/horoscope-predictions/date/${encodeURIComponent(date)}`
    );
    if (!res.ok) throw new Error("horoscope predictions by date failed");
    return res.json();
  } catch {
    return [];
  }
}

export async function getHoroscopePredictionsBySignIdAndDate(
  signId: string,
  date: string
): Promise<HoroscopePrediction[]> {
  try {
    const res = await fetch(
      `${API_BASE}/horoscope-predictions/sign/${encodeURIComponent(
        signId
      )}/date/${encodeURIComponent(date)}`
    );
    if (!res.ok)
      throw new Error("horoscope predictions by sign and date failed");
    return res.json();
  } catch {
    return [];
  }
}

export async function fetchAllBakthiContent(): Promise<BakthiContent[]> {
  try {
    const res = await fetch(`${API_BASE}/bakthi-content`);
    if (!res.ok) throw new Error("bakthi content load failed");
    return res.json();
  } catch {
    return [];
  }
}

export async function getBakthiContent(
  id: number
): Promise<BakthiContent | null> {
  try {
    const res = await fetch(`${API_BASE}/bakthi-content/${id}`);
    if (!res.ok) return null;
    return res.json();
  } catch {
    return null;
  }
}

export async function searchBakthiContent(
  query: string
): Promise<BakthiContent[]> {
  try {
    const res = await fetch(
      `${API_BASE}/bakthi-content/search?q=${encodeURIComponent(query)}`
    );
    if (!res.ok) throw new Error("bakthi content search failed");
    return res.json();
  } catch {
    return [];
  }
}

export async function getBakthiContentByCategory(
  category: string
): Promise<BakthiContent[]> {
  try {
    const res = await fetch(
      `${API_BASE}/bakthi-content/category/${encodeURIComponent(category)}`
    );
    if (!res.ok) throw new Error("bakthi content by category failed");
    return res.json();
  } catch {
    return [];
  }
}

export async function getFeaturedBakthiContent(): Promise<BakthiContent[]> {
  try {
    const res = await fetch(`${API_BASE}/bakthi-content/featured`);
    if (!res.ok) throw new Error("featured bakthi content failed");
    return res.json();
  } catch {
    return [];
  }
}

export async function getBakthiContentCategories(): Promise<string[]> {
  try {
    const res = await fetch(`${API_BASE}/bakthi-content/categories`);
    if (!res.ok) throw new Error("bakthi content categories failed");
    return res.json();
  } catch {
    return [];
  }
}
